/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { HFlow, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { useFlags } from 'config/useFlagsContext'
import { isEmpty } from 'lodash'
import { useMemo } from 'react'

import { ConsultaSeguimentoFieldModel } from '../consulta-seguimento/model-consultaSeguimento'
import { MChatFooterInfo } from '../mchat/components/MChatFooterInfo'
import { MChatResultadoView } from '../mchat/mchat-resultado/MChatResultadoView'
import { MChatAnteriorFooterInfoModel, MChatFieldModel } from '../mchat/model-mchat'
import { MCHAT_RF_ACCORDION_SECTIONS, MChatRfAccordionSectionEnum } from '../model-mChatRf'

export interface MChatRfAccordionSectionProps {
  section: MChatRfAccordionSectionEnum
  value: MChatFieldModel | ConsultaSeguimentoFieldModel
  stepper: React.ReactNode
  primaryActions?: React.ReactNode
  secondaryActions?: React.ReactNode
  isConsultaSeguimentoAtiva?: boolean
  footerInfo?: MChatAnteriorFooterInfoModel
  hasCurrentValue?: boolean
}

export function MChatRfAccordionSection(props: Readonly<MChatRfAccordionSectionProps>) {
  const {
    section,
    value,
    stepper,
    primaryActions,
    secondaryActions,
    isConsultaSeguimentoAtiva,
    footerInfo,
    hasCurrentValue,
  } = props

  const { M_CHAT_RF_ENABLED } = useFlags()
  const theme = useTheme()
  const classes = useMemo(() => createStyles(theme, section, isConsultaSeguimentoAtiva), [
    theme,
    section,
    isConsultaSeguimentoAtiva,
  ])

  const { title, description } = MCHAT_RF_ACCORDION_SECTIONS(M_CHAT_RF_ENABLED)[section]
  const hasValues = !isEmpty(value)
  const hasFooterInfo = !isEmpty(footerInfo) && !hasCurrentValue

  return (
    <HFlow style={classes.section} justifyContent='flex-start' hSpacing={0.625}>
      {stepper}

      <VFlow vSpacing={0.5}>
        <HFlow justifyContent='space-between'>
          <HFlow alignItems='center' hSpacing={1}>
            <Text fontWeight='bold' fontSize={1} style={classes.textColor}>
              {title}
            </Text>
            {primaryActions}
          </HFlow>

          {secondaryActions && <HFlow hSpacing={0.5}>{secondaryActions}</HFlow>}
        </HFlow>
        {!hasValues ? (
          <Text style={classes.textColor}>{description}</Text>
        ) : (
          section === MChatRfAccordionSectionEnum.MCHAT && <MChatResultadoView values={value as MChatFieldModel} />
        )}
        {hasFooterInfo && <MChatFooterInfo mChat={footerInfo} />}
      </VFlow>
    </HFlow>
  )
}

const createStyles = (theme: Theme, section: MChatRfAccordionSectionEnum, isConsultaSeguimentoAtiva: boolean) => ({
  section: css`
    padding: 1rem;
    background-color: ${section === MChatRfAccordionSectionEnum.CONSULTA_SEGUIMENTO &&
    !isConsultaSeguimentoAtiva &&
    theme.pallete.gray.c90};
    border-top: ${section === MChatRfAccordionSectionEnum.MCHAT || isConsultaSeguimentoAtiva
      ? 'none'
      : `1px solid ${theme.pallete.gray.c80}`};
    grid-auto-columns: 1.5rem 1fr;
    position: relative;
  `,
  textColor: css`
    color: ${section === MChatRfAccordionSectionEnum.MCHAT || isConsultaSeguimentoAtiva
      ? theme.pallete.gray.c10
      : theme.pallete.gray.c40};
  `,
})
